/**
 * Notifications — `/notifications` route (Phase G3C-B CP2).
 *
 * Full-history view with filter tabs (All / Unread / Read / Dismissed)
 * + pagination (50 per page) + bulk "Mark all read" affordance.
 *
 * Reuses `AppNav` (which already carries the bell icon) and the same
 * DocumentsShell-style layout wrapper for chrome consistency.
 *
 * Optimistic UI rules mirror the dropdown:
 *   - Mark-read / dismiss / delete on a single row: optimistic
 *     mutation + fire-and-forget POST; on failure the next
 *     `refresh()` reconciles from the server.
 *   - Mark-all-read failure: inline banner at the top of the list.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { CheckCheck, ChevronLeft, ChevronRight, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import AppNav from "@/components/app/AppNav";
import NotificationRow from "@/components/notifications/NotificationRow";
import { EMPTY_STATE_COPY } from "@/components/notifications/utils";
import { api, formatApiError } from "@/api/client";

const PAGE_SIZE = 50;

const FILTERS = [
  { key: "all", label: "All" },
  { key: "unread", label: "Unread" },
  { key: "read", label: "Read" },
  { key: "dismissed", label: "Dismissed" },
];

export default function Notifications() {
  const [filter, setFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [items, setItems] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [markAllError, setMarkAllError] = useState(null);
  const [markAllInFlight, setMarkAllInFlight] = useState(false);

  const fetchPage = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/notifications", {
        params: {
          filter,
          limit: PAGE_SIZE,
          offset: page * PAGE_SIZE,
        },
      });
      setItems(res.data?.items || []);
      setUnreadCount(res.data?.unread_count ?? 0);
    } catch (err) {
      toast.error(formatApiError(err));
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [filter, page]);

  useEffect(() => {
    fetchPage();
  }, [fetchPage]);

  // Reset to page 0 whenever the filter tab changes.
  useEffect(() => {
    setPage(0);
  }, [filter]);

  async function handleMarkRead(notification) {
    if (notification.is_read) return;
    setItems((cur) =>
      cur.map((n) =>
        n.id === notification.id ? { ...n, is_read: true, read_at: new Date().toISOString() } : n,
      ),
    );
    setUnreadCount((c) => Math.max(0, c - 1));
    try {
      await api.post(`/notifications/${notification.id}/read`);
      window.dispatchEvent(new Event("notifications:changed"));
    } catch (err) {
      toast.error(formatApiError(err));
      fetchPage();
    }
  }

  async function handleDismiss(notification) {
    if (notification.is_dismissed) return;
    // Optimistic — remove from view if we're on a non-dismissed tab.
    if (filter !== "dismissed") {
      setItems((cur) => cur.filter((n) => n.id !== notification.id));
    } else {
      setItems((cur) =>
        cur.map((n) =>
          n.id === notification.id ? { ...n, is_dismissed: true, dismissed_at: new Date().toISOString() } : n,
        ),
      );
    }
    try {
      await api.post(`/notifications/${notification.id}/dismiss`);
      window.dispatchEvent(new Event("notifications:changed"));
      toast.success("Notification dismissed.");
    } catch (err) {
      toast.error(formatApiError(err));
      fetchPage();
    }
  }

  async function handleDelete(notification) {
    setItems((cur) => cur.filter((n) => n.id !== notification.id));
    try {
      await api.delete(`/notifications/${notification.id}`);
      window.dispatchEvent(new Event("notifications:changed"));
      toast.success("Notification deleted.");
    } catch (err) {
      toast.error(formatApiError(err));
      fetchPage();
    }
  }

  async function handleMarkAllRead() {
    if (markAllInFlight || unreadCount === 0) return;
    setMarkAllError(null);
    setMarkAllInFlight(true);
    setItems((cur) =>
      cur.map((n) => ({
        ...n,
        is_read: true,
        read_at: n.read_at || new Date().toISOString(),
      })),
    );
    setUnreadCount(0);
    try {
      await api.post("/notifications/read-all");
      window.dispatchEvent(new Event("notifications:changed"));
      // Server-authoritative refresh so counts reconcile.
      fetchPage();
    } catch (_err) {
      setMarkAllError(
        "Some notifications couldn't be marked read. Try again in a moment.",
      );
      fetchPage();
    } finally {
      setMarkAllInFlight(false);
    }
  }

  const hasItems = items.length > 0;
  const canPrev = page > 0;
  const canNext = items.length === PAGE_SIZE;

  return (
    <div
      data-testid="notifications-page-root"
      className="min-h-screen text-v2-text bg-v2-background"
      style={{ background: "var(--v2-background)" }}
    >
      <AppNav />
      <main className="max-w-4xl mx-auto px-6 md:px-10 pt-8 pb-24">
        <header className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="text-xs uppercase tracking-[0.28em] text-v2-textMuted">
              Notifications
            </p>
            <h1 className="mt-2 text-[36px] sm:text-[48px] font-semibold tracking-tight text-v2-text">
              Your activity feed
            </h1>
            <p className="mt-3 text-sm text-v2-textMuted max-w-2xl">
              Deadline reminders, status updates, and new matching grants. Click
              a notification to open the source; use the actions to mark read,
              dismiss, or delete.
            </p>
          </div>
          <button
            type="button"
            onClick={handleMarkAllRead}
            disabled={unreadCount === 0 || markAllInFlight}
            data-testid="notifications-page-mark-all-read"
            className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-semibold border transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            style={{
              background: unreadCount ? "rgba(167,139,250,0.14)" : "transparent",
              borderColor: unreadCount ? "rgba(167,139,250,0.42)" : "var(--v2-border)",
              color: unreadCount ? "var(--v2-accent)" : "var(--v2-textMuted)",
            }}
          >
            {markAllInFlight ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <CheckCheck className="w-3.5 h-3.5" />
            )}
            Mark all read
          </button>
        </header>

        {/* Filter tabs */}
        <nav
          data-testid="notifications-page-filter-tabs"
          className="mt-8 flex items-center gap-1 border-b border-v2-border"
        >
          {FILTERS.map((f) => {
            const active = filter === f.key;
            return (
              <button
                key={f.key}
                type="button"
                onClick={() => setFilter(f.key)}
                data-testid={`notifications-page-filter-${f.key}`}
                data-active={active ? "true" : "false"}
                className={`px-4 py-2 text-v2-caption font-semibold uppercase tracking-[0.14em] transition-colors ${
                  active
                    ? "text-v2-text border-b-2 -mb-px"
                    : "text-v2-textMuted hover:text-v2-text"
                }`}
                style={{
                  borderColor: active ? "var(--v2-accent)" : "transparent",
                }}
              >
                {f.label}
                {f.key === "unread" && unreadCount > 0 && (
                  <span
                    className="ml-2 inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-semibold"
                    style={{
                      background: "rgba(167,139,250,0.14)",
                      color: "var(--v2-accent)",
                    }}
                  >
                    {unreadCount > 99 ? "99+" : unreadCount}
                  </span>
                )}
              </button>
            );
          })}
        </nav>

        {markAllError && (
          <div
            data-testid="notifications-page-mark-all-error"
            role="alert"
            className="mt-4 flex items-start gap-2 rounded-2xl px-4 py-3"
            style={{
              background: "rgba(239,68,68,0.06)",
              border: "1px solid rgba(239,68,68,0.28)",
            }}
          >
            <span className="flex-1 text-sm text-v2-text">{markAllError}</span>
            <button
              type="button"
              onClick={() => setMarkAllError(null)}
              aria-label="Dismiss error"
              className="text-v2-textMuted hover:text-v2-text"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        <div className="mt-6 rounded-2xl overflow-hidden" style={{ background: "var(--v2-surface)", border: "1px solid var(--v2-border)" }}>
          {loading ? (
            <div
              data-testid="notifications-page-loading"
              className="flex items-center gap-2 px-6 py-10 text-sm text-v2-textMuted"
            >
              <Loader2 className="w-4 h-4 animate-spin" />
              Loading notifications…
            </div>
          ) : hasItems ? (
            <ul data-testid="notifications-page-list">
              {items.map((n) => (
                <li key={n.id}>
                  <NotificationRow
                    notification={n}
                    mode="full"
                    onMarkRead={handleMarkRead}
                    onDismiss={handleDismiss}
                    onDelete={handleDelete}
                  />
                </li>
              ))}
            </ul>
          ) : (
            <div
              data-testid="notifications-page-empty"
              className="px-6 py-12 text-center"
            >
              <p className="text-sm text-v2-textMuted max-w-md mx-auto leading-relaxed">
                {filter === "all" ? EMPTY_STATE_COPY : `No ${filter} notifications right now.`}
              </p>
            </div>
          )}
        </div>

        {/* Pagination */}
        {(canPrev || canNext) && (
          <div
            data-testid="notifications-page-pagination"
            className="mt-6 flex items-center justify-between"
          >
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={!canPrev}
              data-testid="notifications-page-prev"
              className="inline-flex items-center gap-1 text-xs font-semibold text-v2-textMuted hover:text-v2-text disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-3.5 h-3.5" /> Previous
            </button>
            <span className="text-xs text-v2-textSubtle">
              Page {page + 1}
            </span>
            <button
              type="button"
              onClick={() => setPage((p) => p + 1)}
              disabled={!canNext}
              data-testid="notifications-page-next"
              className="inline-flex items-center gap-1 text-xs font-semibold text-v2-textMuted hover:text-v2-text disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Next <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
      </main>
    </div>
  );
}
